import { useEffect, useRef, useState } from 'react'
import { useTaskboard } from '../lib/taskboardContext.js'
import { watchComments, watchActivity } from '../lib/data.js'
import {
  dueMeta,
  bellMeta,
  chipStyle,
  tsToDate,
  dateToInput,
  dateInputToDate,
  splitMentions,
  isMention,
  avatarStyle,
} from '../lib/format.js'
import { SlideOver, Button, Field, Chip, Popover, MenuItem, TagLabel } from '../ds/index.js'
import Avatar from './Avatar.jsx'
import DriveLinksField from './DriveLinksField.jsx'
import { Trash } from '@phosphor-icons/react'

function RenderedComment({ text }) {
  return (
    <>
      {splitMentions(text).map((part, i) =>
        isMention(part) ? (
          <span key={i} style={{ color: 'var(--color-gold)', fontWeight: 600 }}>{part}</span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

export default function TaskDetail({ task }) {
  const {
    currentUser,
    currentProject: project,
    users,
    usersById,
    labels,
    labelsById,
    statuses,
    statusesById,
    closeTask,
    setStatus,
    setAssignee,
    setDue,
    updateTaskFields,
    toggleLabelOnTask,
    addCommentToTask,
    deleteTaskById,
    createLabel,
    engagements: allEngagements = [],
  } = useTaskboard()

  const [comments, setComments] = useState([])
  const [activity, setActivity] = useState([])
  const [title, setTitle] = useState(task.title)
  const [description, setDescription] = useState(task.description || '')
  const [commentDraft, setCommentDraft] = useState('')
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [labelMenuOpen, setLabelMenuOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [newLabelMode, setNewLabelMode] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  const [newLabelColor, setNewLabelColor] = useState('#7A7568')
  const titleRef = useRef(null)

  // Re-seed editable fields when the selected task changes.
  useEffect(() => {
    setTitle(task.title)
    setDescription(task.description || '')
    setCommentDraft('')
    setMentionOpen(false)
    setLabelMenuOpen(false)
  }, [task.id])

  // Live comments + activity for this task.
  useEffect(() => watchComments(task.id, setComments), [task.id])
  useEffect(() => watchActivity(task.id, setActivity), [task.id])

  // Auto-grow the title textarea.
  useEffect(() => {
    const el = titleRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
    }
  }, [title])

  const done = !!statusesById[task.status]?.done
  const meta = dueMeta(tsToDate(task.dueDate), done)
  const bell = bellMeta(meta, done)

  const mentionFiltered = users.filter((u) => u.name.toLowerCase().includes(mentionQuery))

  function onCommentChange(value) {
    setCommentDraft(value)
    const m = value.match(/(?:^|\s)@([\w]*)$/)
    if (m) {
      setMentionOpen(true)
      setMentionQuery(m[1].toLowerCase())
    } else {
      setMentionOpen(false)
    }
  }

  function selectMention(u) {
    const val = commentDraft.replace(/(^|\s)@([\w]*)$/, (m, p1) => p1 + '@' + u.name + ' ')
    setCommentDraft(val)
    setMentionOpen(false)
  }

  function submitComment() {
    const text = commentDraft.trim()
    if (!text) return
    addCommentToTask(task.id, text)
    setCommentDraft('')
    setMentionOpen(false)
  }

  function onCommentKey(e) {
    if (mentionOpen && e.key === 'Enter' && mentionFiltered.length) {
      e.preventDefault()
      selectMention(mentionFiltered[0])
      return
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submitComment()
    }
    if (e.key === 'Escape') setMentionOpen(false)
  }

  function commitNewLabel() {
    const t = newLabelName.trim()
    if (t && createLabel) {
      createLabel({ name: t, color: newLabelColor }).then((id) => {
        if (id) toggleLabelOnTask(task, id)
      }).catch(() => {})
      setNewLabelName('')
      setNewLabelMode(false)
      setLabelMenuOpen(false)
    }
  }

  const availableLabels = labels.filter((l) => !(task.labelIds || []).includes(l.id))

  const header = (
    <>
      <span style={{ width: 24, height: 1, background: 'var(--color-gold)' }} />
      <TagLabel color="var(--color-gold)">{project ? project.name : ''}</TagLabel>
      <div style={{ flex: 1 }} />
      <div className="cn-popover-wrap">
        <Button
          variant="subtle"
          size="sm"
          onClick={() => setConfirmDelete((v) => !v)}
          title="Delete task"
          aria-label="Delete task"
        >
          <Trash size={16} />
        </Button>
        <Popover open={confirmDelete} onClose={() => setConfirmDelete(false)} align="right">
          <div style={{ padding: 6, width: 208 }}>
            <div style={{ fontSize: 12, color: 'var(--text-body)', lineHeight: 1.5, marginBottom: 10 }}>Delete this task? This can’t be undone.</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)} style={{ flex: 1 }}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => deleteTaskById(task.id)} style={{ flex: 1 }}>Delete</Button>
            </div>
          </div>
        </Popover>
      </div>
    </>
  )

  const footer = (
    <div style={{ position: 'relative' }}>
      <Popover open={mentionOpen} onClose={() => setMentionOpen(false)} sheet={false} style={{ bottom: 'calc(100% + 6px)', top: 'auto', left: 44 }}>
        <TagLabel color="var(--color-muted)" style={{ display: 'block', padding: '5px 8px 7px' }}>Mention someone</TagLabel>
        {mentionFiltered.map((u) => (
          <MenuItem key={u.uid} onClick={() => selectMention(u)}>
            <Avatar user={u} size={22} />
            {u.name}
          </MenuItem>
        ))}
        {mentionFiltered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '6px 8px' }}>No matches</div>
        )}
      </Popover>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar user={currentUser} size={26} />
        <input
          value={commentDraft}
          onChange={(e) => onCommentChange(e.target.value)}
          onKeyDown={onCommentKey}
          placeholder="Comment… use @ to mention"
          className="cn-input"
          style={{ flex: 1, fontSize: 13, padding: '9px 12px' }}
        />
        <Button onClick={submitComment} size="sm">Send</Button>
      </div>
    </div>
  )

  return (
    <SlideOver
      onClose={closeTask}
      chip={header}
      footer={footer}
    >
      {/* status */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {statuses.map((s) => {
          const active = s.id === task.status
          return (
            <Chip
              key={s.id}
              as="button"
              active={active}
              onClick={() => setStatus(task.id, s.id)}
              style={active ? { background: s.color, borderColor: s.color, color: 'var(--color-white)' } : undefined}
            >
              {s.name}
            </Chip>
          )
        })}
      </div>

      {/* title */}
      <textarea
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => title !== task.title && updateTaskFields(task.id, { title })}
        rows={1}
        className="cn-input font-display"
        style={{ width: '100%', fontSize: 26, lineHeight: 1.05, letterSpacing: '.01em', textTransform: 'uppercase', color: 'var(--color-white)', background: 'transparent', border: 'none', outline: 'none', resize: 'none', padding: 0, marginBottom: 18, overflow: 'hidden' }}
      />

      {/* meta fields */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span className="tag-label" style={{ width: 84, color: 'var(--color-muted)', flexShrink: 0 }}>Assignee</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => updateTaskFields(task.id, { assigneeUid: null })}
              title="Unassigned"
              style={{ width: 26, height: 26, borderRadius: '50%', background: 'transparent', color: 'var(--color-muted)', border: '1.5px dashed var(--border-dash)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, outline: !task.assigneeUid ? '2px solid var(--color-gold)' : '2px solid transparent', outlineOffset: 2, fontSize: 13, lineHeight: 1 }}
            >
              –
            </button>
            {users.map((u) => {
              const on = u.uid === task.assigneeUid
              return (
                <button
                  key={u.uid}
                  onClick={() => setAssignee(task.id, u.uid)}
                  title={u.name + (u.pending ? ' (invited — not signed in yet)' : '')}
                  style={{ ...avatarStyle(u.color, u.ink, 26), cursor: 'pointer', outline: on ? '2px solid var(--color-gold)' : u.pending ? '1px dashed var(--border-dash)' : '2px solid transparent', outlineOffset: '2px', opacity: on ? 1 : u.pending ? 0.5 : 0.55 }}
                >
                  {u.initials}
                </button>
              )
            })}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span className="tag-label" style={{ width: 84, color: 'var(--color-muted)', flexShrink: 0 }}>Due date</span>
          <input
            type="date"
            value={dateToInput(tsToDate(task.dueDate))}
            onChange={(e) => setDue(task.id, dateInputToDate(e.target.value))}
            className="cn-input"
            style={{ width: 'auto', colorScheme: 'dark', fontFamily: 'var(--font-mono)', fontSize: 12, padding: '6px 10px' }}
          />
          {bell.show && (
            <span style={{ color: meta.overdue ? 'var(--color-danger)' : 'var(--color-gold)', fontSize: 11, fontFamily: 'var(--font-mono)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {bell.glyph} {meta.reminderText}
            </span>
          )}
        </div>

        {/* Register item 17: optionally link this task to an engagement, e.g.
            "obtain signed contract" under the National Urban League engagement.
            `engagement:task` was already an allowed association and
            watchEngagementTasks already existed — this is the missing picker.
            Kaycia said to skip it if it got complex; it did not. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span className="tag-label" style={{ width: 84, color: 'var(--color-muted)', flexShrink: 0 }}>Engagement</span>
          <select
            value={task.engagementId || ''}
            onChange={(e) => updateTaskFields(task.id, { engagementId: e.target.value || null })}
            className="cn-input"
            style={{ width: 'auto', maxWidth: 260, fontSize: 12, padding: '6px 10px' }}
          >
            <option value="">Not linked</option>
            {allEngagements.map((en) => (
              <option key={en.id} value={en.id}>
                {en.title || 'Untitled'}{en.org ? ` — ${en.org}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '6px 0' }}>
          <span className="tag-label" style={{ width: 84, color: 'var(--color-muted)', flexShrink: 0, marginTop: 5 }}>Labels</span>
          <div className="cn-popover-wrap" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {(task.labelIds || []).map((id) =>
              labelsById[id] ? (
                <Chip key={id} style={{ ...chipStyle(labelsById[id].color), alignItems: 'center' }}>
                  {labelsById[id].name}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleLabelOnTask(task, id)
                    }}
                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0 0 3px', fontSize: 12, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </Chip>
              ) : null,
            )}
            <button
              onClick={() => setLabelMenuOpen((v) => !v)}
              style={{ background: 'none', border: '1px dashed var(--border-dash)', color: 'var(--color-muted)', borderRadius: 'var(--radius-sm)', fontSize: 10, padding: '3px 8px', cursor: 'pointer', letterSpacing: '.04em', textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}
            >
              + Label
            </button>
            <Popover
              open={labelMenuOpen}
              onClose={() => { setLabelMenuOpen(false); setNewLabelMode(false); setNewLabelName('') }}
              style={{ minWidth: 150 }}
            >
              {availableLabels.map((l) => (
                <MenuItem
                  key={l.id}
                  onClick={() => {
                    toggleLabelOnTask(task, l.id)
                    setLabelMenuOpen(false)
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color, flexShrink: 0 }} />
                  {l.name}
                </MenuItem>
              ))}
              {availableLabels.length === 0 && !newLabelMode && (
                <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '6px 8px' }}>All labels applied</div>
              )}
              {createLabel && (
                <div style={{ borderTop: '1px solid var(--color-divider-dark)', marginTop: 4, paddingTop: 4 }}>
                  {newLabelMode ? (
                    <div style={{ padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        value={newLabelName}
                        onChange={(e) => setNewLabelName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitNewLabel()
                          if (e.key === 'Escape') { setNewLabelMode(false); setNewLabelName('') }
                        }}
                        autoFocus
                        placeholder="Label name"
                        className="cn-input"
                        style={{ fontSize: 12, padding: '5px 7px' }}
                      />
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={newLabelColor}
                          onChange={(e) => setNewLabelColor(e.target.value)}
                          style={{ width: 26, height: 26, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                        />
                        <Button size="sm" onClick={commitNewLabel} style={{ flex: 1 }}>Create</Button>
                      </div>
                    </div>
                  ) : (
                    <MenuItem onClick={() => setNewLabelMode(true)} style={{ color: 'var(--color-muted)' }}>
                      ＋ Create label
                    </MenuItem>
                  )}
                </div>
              )}
            </Popover>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
          <span className="tag-label" style={{ width: 84, color: 'var(--color-muted)', flexShrink: 0 }}>Phase</span>
          <select
            value={task.phase || ''}
            onChange={(e) => updateTaskFields(task.id, { phase: e.target.value || null })}
            className="cn-input"
            style={{ width: 'auto', fontSize: 13, padding: '8px 10px' }}
          >
            <option value="">No phase</option>
            <option value="p1">Phase 1 · 30-day</option>
            <option value="p2">Phase 2 · 60-day</option>
            <option value="p3">Phase 3 · 90-day</option>
          </select>
        </div>
      </div>

      {/* description */}
      <Field
        as="textarea"
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={() => description !== (task.description || '') && updateTaskFields(task.id, { description })}
        placeholder="Add a description…"
        rows={4}
        style={{ fontSize: 13, lineHeight: 1.6, resize: 'vertical' }}
      />

      {/* drive files (Phase 5) */}
      <div style={{ marginTop: 16, marginBottom: 26 }}>
        <DriveLinksField
          links={task.driveLinks || []}
          legacyUrl={task.driveUrl || null}
          onSave={(next) => updateTaskFields(task.id, { driveLinks: next })}
        />
      </div>

      {/* activity */}
      <div className="eyebrow" style={{ marginBottom: 14 }}>
        <TagLabel color="var(--color-gold)">Activity</TagLabel>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 26 }}>
        {activity.map((act) => (
          <div key={act.id} style={{ display: 'flex', alignItems: 'baseline', gap: 9, fontSize: 12 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--color-gold)', flexShrink: 0, transform: 'translateY(-1px)' }} />
            <span style={{ color: 'var(--text-body)' }}>{act.text}</span>
          </div>
        ))}
      </div>

      {/* comments */}
      <div style={{ borderTop: '1px solid var(--color-divider-dark)', paddingTop: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 18 }}>
          <TagLabel color="var(--color-gold)">Comments</TagLabel>
          <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)', marginLeft: 9 }}>{comments.length}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {comments.map((c) => {
            const author = usersById[c.authorUid]
            return (
              <div key={c.id} style={{ display: 'flex', gap: 11 }}>
                <Avatar user={author} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-white)' }}>{author?.name || 'Unknown'}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-body)', lineHeight: 1.55 }}>
                    <RenderedComment text={c.text} />
                  </div>
                </div>
              </div>
            )
          })}
          {comments.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>No comments yet. Start the thread below.</div>
          )}
        </div>
      </div>
    </SlideOver>
  )
}
